import React, { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import {
  ChevronLeft, ChevronRight, List, X, ArrowClockwise, SunFill, MoonFill,
} from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';
import { useLocale } from '../i18n/LocaleContext';
import './PortalShell.scss';

// Dashboard shell: the admin panel's sidebar (admin/src/pages/admin/AdminLayout.js)
// — so the three web apps share one navigation.
//
//   items     → [{ key, label, Icon, to, badge? }], one flat menu; every page is its
//               own route, like the admin's, so a reload stays on it
//   active    → key of the current item
//   onRefresh → async; re-fetches the current page's data in place
//   theme     → what usePortalTheme() returns; the shell owns only the button
//   footerTop → optional block at the top of the footer
//   actions   → extra footer buttons after the admin's own
//               [{ key, label, Icon, onClick, danger? }]
//
// Like the admin, the rail collapses to icons on desktop, and on phones the
// whole sidebar becomes a top bar + overlay menu.

const COLLAPSED_KEY = 'creators_sidebar_collapsed';
const THEME_KEY = 'portal_theme';

// Round flag SVGs for the language switcher — the same ones the admin uses.
const FLAG_SRC = {
  hu: 'https://globalartinc.github.io/round-flags/flags/hu.svg',
  en: 'https://globalartinc.github.io/round-flags/flags/en.svg',
};

// localStorage can throw (private mode, blocked site data) — the setting then
// just isn't remembered.
function readSetting(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSetting(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* not persisted — see readSetting */
  }
}

// Light / dark theme for the dashboard. Dark is a class on <html> rather than
// on the shell, so what is portalled to <body> (modals) and
// the loading screens before the shell mounts are themed too. Removed on
// unmount: the marketing pages and login stay light.
//
// Light by default — the portals have always been light; the choice is then
// remembered, like the admin's.
export function usePortalTheme() {
  const [isDark, setIsDark] = useState(() => readSetting(THEME_KEY) === 'dark');

  // Layout effect, so the first dark frame is never painted light.
  useLayoutEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('portal-dark', isDark);
    return () => root.classList.remove('portal-dark');
  }, [isDark]);

  const toggleTheme = useCallback(() => {
    setIsDark((prev) => {
      writeSetting(THEME_KEY, prev ? 'light' : 'dark');
      return !prev;
    });
  }, []);

  return { isDark, toggleTheme };
}

export default function PortalShell({ items, active, onRefresh, theme, footerTop, actions = [], children }) {
  const { lang, setLang, t } = useLocale();
  const [collapsed, setCollapsed] = useState(() => readSetting(COLLAPSED_KEY) === '1');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { isDark, toggleTheme } = theme;

  // Freeze the page behind the mobile menu overlay.
  useEffect(() => {
    document.body.classList.toggle('portal-menu-open', mobileOpen);
    return () => document.body.classList.remove('portal-menu-open');
  }, [mobileOpen]);

  function toggleCollapsed() {
    const next = !collapsed;
    setCollapsed(next);
    writeSetting(COLLAPSED_KEY, next ? '1' : '0');
  }

  // Refresh re-fetches in place instead of reloading the browser: the page,
  // the brand and anything scrolled stay put, and nothing flashes blank. The
  // icon spins for at least a beat, so a fast answer still reads as "done".
  async function handleRefresh() {
    if (refreshing) return;
    setMobileOpen(false);
    setRefreshing(true);
    try {
      await Promise.all([onRefresh(), new Promise((resolve) => setTimeout(resolve, 600))]);
    } catch {
      /* the page shows its own error state */
    } finally {
      setRefreshing(false);
    }
  }

  const refreshIcon = <ArrowClockwise className={refreshing ? 'portal-refresh-spin' : undefined} />;

  return (
    <div className={`portal-shell${collapsed ? ' portal-shell--collapsed' : ''}`}>
      <aside
        className={`portal-sidebar${collapsed ? ' portal-sidebar--collapsed' : ''}${
          mobileOpen ? ' portal-sidebar--mobile-open' : ''
        }`}
      >
        <div className="portal-sidebar-head">
          <div className="portal-sidebar-logo">
            Outfino
            <span className="portal-sidebar-logo-sub">Creators</span>
          </div>
          <button
            type="button"
            className="portal-sidebar-toggle"
            onClick={toggleCollapsed}
            title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
            aria-label={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </button>
          <button
            type="button"
            className="portal-mobile-toggle"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label={mobileOpen ? t('sidebar.menuClose') : t('sidebar.menu')}
          >
            {mobileOpen ? <X /> : <List />}
          </button>
          <button
            type="button"
            className="portal-mobile-refresh"
            onClick={handleRefresh}
            disabled={refreshing}
            aria-label={t('shell.refresh')}
          >
            {refreshIcon}
          </button>
        </div>

        <nav className="portal-nav">
          {items.map(({ key, label, Icon, to, badge }) => (
            <Link
              key={key}
              to={to}
              title={label}
              onClick={() => setMobileOpen(false)}
              aria-current={active === key ? 'page' : undefined}
              className={`portal-nav-item${active === key ? ' portal-nav-item--active' : ''}`}
            >
              <Icon className="portal-nav-icon" />
              <span className="portal-nav-label">{label}</span>
              {badge > 0 && (
                <span className="portal-nav-badge" aria-label={t('shell.newCount', { count: badge })}>
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </Link>
          ))}
        </nav>

        <div className="portal-sidebar-footer">
          {footerTop}
          <button
            type="button"
            className="portal-sidebar-btn"
            onClick={handleRefresh}
            disabled={refreshing}
            title={t('shell.refresh')}
          >
            {refreshIcon}
            <span className="portal-nav-label">{t('shell.refresh')}</span>
          </button>
          <button
            type="button"
            className="portal-sidebar-btn"
            onClick={() => setLang(lang === 'hu' ? 'en' : 'hu')}
            title={t('lang.switchTitle')}
          >
            <img
              className="portal-lang-flag"
              src={FLAG_SRC[lang]}
              alt={lang === 'hu' ? 'Magyar' : 'English'}
              width={18}
              height={18}
            />
            <span className="portal-nav-label">{t('lang.name')}</span>
          </button>
          <button
            type="button"
            className="portal-sidebar-btn"
            onClick={toggleTheme}
            title={isDark ? t('theme.lightTitle') : t('theme.darkTitle')}
          >
            {isDark ? <SunFill /> : <MoonFill />}
            <span className="portal-nav-label">{isDark ? t('theme.light') : t('theme.dark')}</span>
          </button>
          {actions.map(({ key, label, Icon, onClick, danger }) => (
            <button
              key={key}
              type="button"
              className={`portal-sidebar-btn${danger ? ' portal-sidebar-btn--danger' : ''}`}
              onClick={() => {
                setMobileOpen(false);
                onClick();
              }}
              title={label}
            >
              <Icon />
              <span className="portal-nav-label">{label}</span>
            </button>
          ))}
        </div>
      </aside>

      <main className="portal-main">{children}</main>
    </div>
  );
}
